import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProvider } from '../../contexts/ProviderContext';
import { useToast } from '../../contexts/ToastContext';
import {
  fetchProviderOrders,
  ProviderOrder,
  ProviderOrderStats,
  formatCurrency,
  formatDate,
  getStatusColor,
} from '../../lib/providerOrders';
import {
  ShoppingBagIcon,
  ArrowRightIcon,
  ClockIcon,
  TruckIcon,
  BanknotesIcon,
} from '@heroicons/react/24/outline';
import ProviderLayout from './ProviderLayout';

const ProviderOrders: React.FC = () => {
  const navigate = useNavigate();
  const { currentProviderId } = useProvider();
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState<ProviderOrder[]>([]);
  const [stats, setStats] = useState<ProviderOrderStats>({
    total_orders: 0,
    pending_orders: 0,
    processing_orders: 0,
    shipped_orders: 0,
    delivered_orders: 0,
    total_revenue: 0,
    avg_order_value: 0,
  });

  useEffect(() => {
    if (currentProviderId) {
      loadOrders();
    }
  }, [currentProviderId]);

  const loadOrders = async () => {
    if (!currentProviderId) return;

    try {
      setLoading(true);
      const result = await fetchProviderOrders({
        provider_id: currentProviderId,
        limit: 10, // Show recent 10 orders on dashboard
        offset: 0,
      });
      setOrders(result.orders);
      setStats(result.stats);
    } catch (err) {
      console.error('Error loading orders:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (order: ProviderOrder) => {
    const status = order.latest_provider_update?.status || order.display_fulfillment_status || 'pending';
    const color = getStatusColor(status);
    const colorClasses: Record<string, string> = {
      emerald: 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20',
      yellow: 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/20',
      blue: 'bg-blue-500/10 text-blue-400 border border-blue-500/20',
      indigo: 'bg-indigo-500/10 text-indigo-400 border border-indigo-500/20',
      purple: 'bg-purple-500/10 text-purple-400 border border-purple-500/20',
      green: 'bg-green-500/10 text-green-400 border border-green-500/20',
      red: 'bg-red-500/10 text-red-400 border border-red-500/20',
      orange: 'bg-orange-500/10 text-orange-400 border border-orange-500/20',
      gray: 'bg-white/[0.06] text-white/50 border border-white/15',
    };

    const displayStatus = status.replace(/_/g, ' ').toUpperCase();

    return (
      <span className={`px-2.5 py-1 text-xs  font-semibold rounded-full ${colorClasses[color] || colorClasses.gray}`}>
        {displayStatus}
      </span>
    );
  };

  return (
    <ProviderLayout>
      <div className="max-w-7xl mx-auto">
            {/* Header */}
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-white ">ORDERS</h1>
              <p className="text-white/50 mt-2">Manage and track your sales orders</p>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className=" p-6 rounded-lg shadow border border-white/[0.08]">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm  text-white/50 uppercase">Total Sales (30d)</p>
                    <p className="text-2xl font-bold text-white mt-2">
                      {formatCurrency(stats.total_revenue, 'USD')}
                    </p>
                  </div>
                  <BanknotesIcon className="w-10 h-10 text-emerald-400" />
                </div>
              </div>

              <div className=" p-6 rounded-lg shadow border border-white/[0.08]">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm  text-white/50 uppercase">Total Orders (30d)</p>
                    <p className="text-2xl font-bold text-white mt-2">{stats.total_orders}</p>
                  </div>
                  <ShoppingBagIcon className="w-10 h-10 text-blue-600" />
                </div>
              </div>

              <div className=" p-6 rounded-lg shadow border border-white/[0.08]">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm  text-white/50 uppercase">Pending Orders</p>
                    <p className="text-2xl font-bold text-yellow-600 mt-2">{stats.pending_orders}</p>
                  </div>
                  <ClockIcon className="w-10 h-10 text-yellow-600" />
                </div>
              </div>

              <div className=" p-6 rounded-lg shadow border border-white/[0.08]">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm  text-white/50 uppercase">Shipped Orders</p>
                    <p className="text-2xl font-bold text-purple-600 mt-2">{stats.shipped_orders}</p>
                  </div>
                  <TruckIcon className="w-10 h-10 text-purple-600" />
                </div>
              </div>
            </div>

            {/* Recent Orders Section */}
            <div className=" rounded-lg shadow border border-white/[0.08]">
              <div className="p-6 border-b border-white/[0.08]">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold text-white ">RECENT ORDERS</h2>
                  <button
                    onClick={() => navigate('/provider/orders/list')}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-black text-white rounded  text-sm hover:bg-gray-800 transition-colors"
                  >
                    View All Orders
                    <ArrowRightIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {loading ? (
                <div className="p-12 text-center">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                  <p className="mt-4 text-white/50 ">Loading orders...</p>
                </div>
              ) : orders.length === 0 ? (
                <div className="p-12 text-center">
                  <ShoppingBagIcon className="w-16 h-16 mx-auto text-white/25 mb-4" />
                  <p className="text-white/50 ">No orders yet</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-transparent border-b border-white/[0.08]">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs  font-semibold text-white/70 uppercase tracking-wider">
                          Order #
                        </th>
                        <th className="px-6 py-3 text-left text-xs  font-semibold text-white/70 uppercase tracking-wider">
                          Merchant
                        </th>
                        <th className="px-6 py-3 text-left text-xs  font-semibold text-white/70 uppercase tracking-wider">
                          Date
                        </th>
                        <th className="px-6 py-3 text-left text-xs  font-semibold text-white/70 uppercase tracking-wider">
                          Items
                        </th>
                        <th className="px-6 py-3 text-left text-xs  font-semibold text-white/70 uppercase tracking-wider">
                          Total
                        </th>
                        <th className="px-6 py-3 text-left text-xs  font-semibold text-white/70 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs  font-semibold text-white/70 uppercase tracking-wider">
                          Action
                        </th>
                      </tr>
                    </thead>
                    <tbody className=" divide-y divide-white/[0.06]">
                      {orders.map((order) => (
                        <tr
                          key={order.order_id}
                          className="hover:bg-white/[0.03] transition-colors cursor-pointer"
                          onClick={() => navigate(`/provider/orders/${order.order_id}`)}
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className=" text-sm font-semibold text-white">
                              {order.order_number}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <p className="text-sm font-medium text-white">{order.merchant_shop_name}</p>
                              <p className="text-xs text-white/35">{order.merchant_shop_domain}</p>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-white/50 ">
                            {formatDate(order.created_at)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-white ">
                            {order.line_items_count}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-white ">
                            {formatCurrency(order.current_total_price_amount, order.currency_code)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">{getStatusBadge(order)}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/provider/orders/${order.order_id}`);
                              }}
                              className="text-orange-400 hover:text-primary-900  font-semibold"
                            >
                              View Details →
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
      </div>
    </ProviderLayout>
  );
};

export default ProviderOrders;
